import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/image";

const CATEGORY_COLORS = {
  Cyberpunk: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/20",
  Corporate: "text-white/80 bg-white/5 border-white/10",
  Editorial: "text-white/80 bg-white/5 border-white/10",
  Anime: "text-[#E0007A] bg-[#E0007A]/10 border-[#E0007A]/25",
  Vintage: "text-amber-200 bg-amber-200/10 border-amber-200/20",
  Minimalist: "text-white/70 bg-white/5 border-white/10",
  Street: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/20",
  Fantasy: "text-violet-300 bg-violet-300/10 border-violet-300/20",
  Cottagecore: "text-amber-100 bg-amber-100/10 border-amber-100/20",
};

function MiniCard({ prompt }) {
  const [loaded, setLoaded] = useState(false);
  const catStyle =
    CATEGORY_COLORS[prompt.style_category] ||
    "text-white/70 bg-white/5 border-white/10";
  return (
    <Link
      to={`/prompt/${prompt.id}`}
      data-testid={`recent-card-${prompt.id}`}
      className="snap-start w-[75vw] xs:w-72 sm:w-64 flex-shrink-0 rounded-2xl overflow-hidden bg-[#0E0E12] border border-white/10 hover:border-white/25 transition-colors block"
    >
      <div className="px-4 pt-3 pb-2">
        <span
          data-testid={`recent-card-label-${prompt.id}`}
          className={`inline-flex items-center text-xs sm:text-sm font-extrabold uppercase px-2.5 py-1 rounded-md border ${catStyle}`}
          style={{ letterSpacing: "0.06em" }}
        >
          {prompt.style_category}
        </span>
      </div>
      <div className="relative aspect-[4/5] bg-[#17171C]">
        {!loaded && (
          <div className="absolute inset-0 animate-pulse bg-white/[0.02]" />
        )}
        <img
          src={resolveImageUrl(prompt.image_url)}
          alt={prompt.title}
          width={400}
          height={500}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0E0E12] to-transparent" />
      </div>
      <div className="p-4">
        <h3 className="font-display text-base leading-tight line-clamp-2 min-h-[2.5rem]">
          {prompt.title}
        </h3>
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-mono-label text-[#00E5FF]">
          Open <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
}

export default function RecentStrip({ limit = 10 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api
      .get("/prompts", { params: { limit, skip: 0 } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.9;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="recent"
      data-testid="recent-strip"
      className="max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 md:mb-10">
        <div>
          <div className="font-mono-label text-[#E0007A]">02 — Fresh drops</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-2 leading-[1.05]">
            The 10 most recent
            <br className="hidden md:block" />
            <span className="text-white/50"> prompts, in order.</span>
          </h2>
        </div>

        {/* Desktop scroll controls */}
        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid="recent-scroll-prev"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="h-10 w-10 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center text-white/70 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="recent-scroll-next"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="h-10 w-10 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center text-white/70 hover:text-white transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      ) : (
        <div
          ref={scrollerRef}
          data-testid="recent-scroller"
          className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 md:-mx-10 px-5 md:px-10 scroll-smooth"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((p) => (
            <MiniCard key={p.id} prompt={p} />
          ))}
          {/* trailing spacer so last card can snap to left edge */}
          <div className="flex-shrink-0 w-1" aria-hidden />
        </div>
      )}
    </section>
  );
}
